<script setup lang="ts">
import { computed, useSlots } from "vue";
import BaseIcon from "./BaseIcon.vue";

type ButtonVariant =
  | "primary"
  | "primary-glass"
  | "secondary"
  | "danger"
  | "danger-solid"
  | "danger-glass"
  | "outline"
  | "ghost"
  | "accent-ghost"
  | "accent-outline"
  | "info"
  | "info-solid"
  | "success"
  | "success-solid"
  | "warning"
  | "transparent";

type ButtonAlign = "start" | "center" | "end";
type ButtonDisplay = "inline-flex" | "flex" | "grid";

const props = withDefaults(defineProps<{
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  label?: string;
  loadingLabel?: string;
  block?: boolean;
  rounded?: boolean;
  align?: ButtonAlign;
  display?: ButtonDisplay;
}>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
  block: false,
  rounded: false,
  align: "center",
  display: "inline-flex",
  loadingLabel: "Loading",
});

const emit = defineEmits<{
  click: [payload: MouseEvent];
}>();

const slots = useSlots();
const isIconOnly = computed(() => (props.icon || props.loading) && !props.label && !slots.default);

const displayClass = computed(() => {
  if (props.display === "grid") return "grid";
  if (props.display === "flex") return "flex items-center";
  return "inline-flex items-center";
});

const baseClasses = computed(() =>
  [
    "font-semibold transition-all duration-200 outline-none cursor-pointer",
    "focus-visible:ring-2 focus-visible:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed",
    "active:scale-95 whitespace-nowrap",
    props.rounded ? "rounded-full" : "rounded-lg",
  ].join(" "),
);

const alignmentClass = computed(() => {
  if (props.display === "grid") {
    if (props.align === "start") return "place-items-start";
    if (props.align === "end") return "place-items-end";
    return "place-items-center";
  }
  if (props.align === "start") return "justify-start";
  if (props.align === "end") return "justify-end";
  return "justify-center";
});

const variantClasses = computed(() => {
  switch (props.variant) {
    case "primary":
      return "bg-primary text-white hover:bg-primary-hover shadow-md shadow-primary/20 focus:ring-primary/50 border border-transparent";
    case "primary-glass":
      return "bg-primary/10 text-primary-400 hover:bg-primary/20 hover:text-primary-300 ring-1 ring-inset ring-primary-400/10 border-transparent focus:ring-primary-500/50";
    case "secondary":
      return "bg-surface-muted text-ui-muted hover:bg-surface-elevated border border-ui focus:ring-ui-focus";
    case "danger":
      return "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 border border-red-100 dark:border-red-900/30 focus:ring-red-200 dark:focus:ring-red-800";
    case "danger-solid":
      return "bg-red-600 text-white hover:bg-red-700 shadow-sm shadow-red-900/20 focus:ring-red-500 border border-transparent";
    case "danger-glass":
      return "bg-red-500/15 text-red-400 hover:bg-red-500/25 hover:text-red-300 ring-1 ring-inset ring-red-400/10 border-transparent focus:ring-red-500/50";
    case "success":
      return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-100 dark:border-emerald-900/30 focus:ring-emerald-200 dark:focus:ring-emerald-800";
    case "success-solid":
      return "bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-900/20 focus:ring-emerald-500 border border-transparent";
    case "info":
      return "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 border border-blue-100 dark:border-blue-900/30 focus:ring-blue-200 dark:focus:ring-blue-800";
    case "info-solid":
      return "bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-900/20 focus:ring-blue-500 border border-transparent";
    case "warning":
      return "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 border border-amber-100 dark:border-amber-900/30 focus:ring-amber-200 dark:focus:ring-amber-800";
    case "outline":
      return "bg-transparent border border-primary text-primary hover:bg-primary/5 focus:ring-primary/30 dark:border-dark-200 dark:text-dark-text-muted dark:hover:bg-dark-100";
    case "ghost":
      return "bg-transparent text-ui-muted hover:bg-surface-muted hover:text-ui border border-transparent focus:ring-ui-focus";
    case "accent-ghost":
      return "bg-transparent text-ui-muted hover:bg-primary/15 hover:text-primary-light border border-transparent focus:ring-primary/40";
    case "accent-outline":
      return "bg-surface-muted text-ui-muted hover:bg-primary/15 hover:text-primary-light border border-ui focus:ring-primary/40";
    case "transparent":
      return "focus:ring-0";
    default:
      return "bg-primary text-white hover:bg-primary-hover";
  }
});

const sizeClasses = computed(() => {
  if (isIconOnly.value) {
    if (props.size === "sm") return "w-8 h-8 p-0";
    if (props.size === "lg") return "w-12 h-12 p-0";
    return "w-10 h-10 p-0";
  }

  if (props.size === "sm") return "px-4 py-1.5 text-xs gap-1";
  if (props.size === "lg") return "px-8 py-3 text-base gap-2.5";
  return "px-6 py-2 text-sm gap-1.5";
});

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.loading) emit("click", event);
}
</script>

<template>
  <button
    :type="type"
    :data-variant="variant"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
    :aria-label="loading && !label && !$slots.default ? loadingLabel : undefined"
    :class="[displayClass, baseClasses, alignmentClass, variantClasses, sizeClasses, block ? 'w-full' : '', $attrs.class]"
    @click="handleClick"
  >
    <BaseIcon v-if="loading" name="Loader2" class="animate-spin" decorative />
    <BaseIcon v-if="icon && !loading" :name="icon" :size="size === 'sm' ? 16 : 18" />
    <slot v-if="!loading && $slots.start && !icon" name="start" />
    <span v-if="label">{{ label }}</span>
    <slot v-else />
    <slot name="end" />
  </button>
</template>

<style scoped>
/* Secondary actions must keep their neutral state when a consumer adds a
   broad dark-theme button:hover rule. Use the same semantic tokens as the
   utility variant so the primitive owns both resting and hover states. */
button[data-variant="secondary"] {
  background-color: var(--color-surface-muted, #f9fafb);
  border-color: var(--color-border, #e5e7eb);
  color: var(--color-text-muted, #374151);
}

button[data-variant="secondary"]:hover:not(:disabled) {
  background-color: var(--color-surface-elevated, #ffffff);
  border-color: var(--color-border-strong, #d1d5db);
  color: var(--color-text, #111827);
}

:global(.dark button[data-variant="secondary"]) {
  background-color: var(--color-surface-muted, #0f172a);
  border-color: var(--color-border-strong, #374151);
  color: var(--color-text-muted, #d1d5db);
}

:global(.dark button[data-variant="secondary"]:hover:not(:disabled)) {
  background-color: var(--color-surface-elevated, #1f2937);
  border-color: var(--color-border-strong, #4b5563);
  color: var(--color-text, #f3f4f6);
}

/* A consumer's broad `button:hover` rule must never turn a destructive action
   into a primary/success-colored action. The semantic selector intentionally
   outranks generic dark-theme hover selectors. */
button[data-variant="danger"] {
  background-color: #fef2f2;
  border-color: #fee2e2;
  color: #dc2626;
}

button[data-variant="danger"]:hover:not(:disabled) {
  background-color: #fee2e2;
  border-color: #fecaca;
  color: #b91c1c;
}

:global(.dark button[data-variant="danger"]) {
  background-color: rgb(127 29 29 / 32%);
  border-color: rgb(248 113 113 / 35%);
  color: #fca5a5;
}

:global(.dark button[data-variant="danger"]:hover:not(:disabled)) {
  background-color: rgb(127 29 29 / 58%);
  border-color: rgb(252 165 165 / 58%);
  color: #fee2e2;
}

button[data-variant="danger-solid"]:hover:not(:disabled) {
  background-color: #b91c1c;
  border-color: #b91c1c;
  color: #ffffff;
}

button[data-variant="danger-solid"] {
  background-color: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
}

button[data-variant="danger-glass"]:hover:not(:disabled) {
  background-color: rgb(239 68 68 / 25%);
  color: #fca5a5;
}
</style>
